<template>
	<ProductPage
		:parametrics="parametrics"
		:image-gallery="imageGallery"
		:product-attributes="productAttributes"
		:product-details="productDetails"
		:color-from-parametrics-in-link="colorFromParametricsInLink"
		:special-price="specialPrice"
		:product-recommendations="productRecommendations"
	/>
</template>

<script setup lang="ts">
import { queryToSelectedParametrics } from "~/utils/parametricsUrl";
import type {
	GroupedParametrics,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import type {
	BlokGenericForComponents,
	BlokSEOApp,
	SpecialPrice
} from "~/types/storyblok/storyblok";
import { getLowestPriceIncVat } from "~/utils/product/getLowestPriceIncVat";
import { getStoryblokVersion } from "~/utils/storyblok/getStoryblokVersion";

const {
	getProductByUniqueName,
	getProductParametrics,
	getFirstSecondParametricWithStock,
	selectProductVariant,
	getFirstColorParametricWithStock,
	setColorParametricsInStock,
	getSecondParametricName,
	listHistoricalBestPrices,
	getProductRelations
} = useNorcePwa();

const appStore = useAppStore();
const storyblokApi = useStoryblokApi();
const { setPageTitle, setPageType } = usePageStore();
const route = useRoute();
const requestURL = useRequestURL();
const { getProductStock } = useNorcePwa();

const parametrics = ref<GroupedParametrics>();
const { storeContext, currentCurrency } = storeToRefs(useNorceStore());
const { fetchAllParametrics } = useAllParametrics();
const { locale, t } = useI18n();
const { productDetail } = useGtmEvents();
const { stores } = storeToRefs(useWarehousesAndStores());
const imageGallery = ref<BlokGenericForComponents[]>();
const productAttributes = ref<BlokGenericForComponents[]>();
const productDetails = ref<BlokGenericForComponents[]>();
const specialPrice = ref<SpecialPrice | null>(null);
const seo = ref<BlokSEOApp>();
const colorFromParametricsInLink = ref<ParametricListValue>();
const productRecommendations = ref<BlokGenericForComponents[]>();

const product = useProductStore().products;

if (product) {
	productDetail(product);
}

const getProduct = async () => {
	const { data } = await storyblokApi.get("cdn/stories/product-page-layout", {
		version: getStoryblokVersion(),
		resolve_links: "url"
	});

	imageGallery.value = data.story.content.body[0].image_gallery;
	productAttributes.value = data.story.content.body[0].product_attributes;
	productDetails.value = data.story.content.body[0].product_details;
	seo.value = data.story.content.SEO;
	specialPrice.value = {
		background:
			data.story.content.body[0].special_price_background_color?.value || "",
		text: data.story.content.body[0].special_price_text_color?.value || "",
		enabled: !!data.story.content.body[0].show_special_price_end_date
	};
	productRecommendations.value =
		data.story.content.body[0].product_recommendations;
};
const fetchProduct = async () => {
	const storesIds = stores?.value?.[locale.value]?.map((store) => store.Id);
	const [productsFetched, parametricsFetched] = await Promise.all([
		getProductByUniqueName({
			uniqueName: route.params.uniqueProductName
				? (route.params.uniqueProductName as string)
				: (route.params.slug as string),
			...(storesIds?.length && { storeSeed: storesIds.toString() }),
			...storeContext.value
		}),
		fetchAllParametrics(),
		getProduct()
	]);

	const productParametrics = await getProductParametrics(
		{
			...storeContext.value
		},
		{ product: productsFetched, allParametrics: parametricsFetched }
	);
	if (productParametrics.color) {
		productParametrics.color = setColorParametricsInStock({
			parametrics: productParametrics,
			products: productsFetched
		});
	}

	parametrics.value = productParametrics;
	let selectedColorLocal: ParametricListValue | undefined,
		selectedSecondParametricLocal: ParametricListValue | undefined;

	const slugSelected = queryToSelectedParametrics(
		route.query as Record<string, unknown>,
		productParametrics
	);
	if (
		slugSelected.selectedColor !== undefined ||
		slugSelected.selectedSecondParametric !== undefined
	) {
		selectedColorLocal =
			slugSelected.selectedColor ??
			getFirstColorParametricWithStock({
				parametrics: productParametrics,
				products: productsFetched
			});
		colorFromParametricsInLink.value = slugSelected.selectedColor;
		const secondParam =
			productParametrics[getSecondParametricName(productParametrics)];
		if (secondParam) {
			selectedSecondParametricLocal =
				slugSelected.selectedSecondParametric ??
				getFirstSecondParametricWithStock({
					parametrics: secondParam,
					products: productsFetched,
					selectedColor: selectedColorLocal
				});
		}
	} else {
		selectedColorLocal = getFirstColorParametricWithStock({
			parametrics: productParametrics,
			products: productsFetched
		});
		const secondParamTemp =
			productParametrics[getSecondParametricName(productParametrics)];
		if (secondParamTemp) {
			selectedSecondParametricLocal = getFirstSecondParametricWithStock({
				parametrics: secondParamTemp,
				products: productsFetched,
				selectedColor: selectedColorLocal
			});
		}
	}

	useProductStore().addSelectedColor(selectedColorLocal);

	if (selectedSecondParametricLocal) {
		useProductStore().addSelectedSecondParametric(
			selectedSecondParametricLocal
		);
	}

	useProductStore().addProducts(productsFetched);
	useProductStore().addParametrics(productParametrics);

	const selectedProduct = selectProductVariant({
		selectedColorId: selectedColorLocal?.Id,
		selectedSecondParametricId: selectedSecondParametricLocal?.Id,
		products: productsFetched,
		parametricsAvailable:
			productParametrics?.color ??
			productParametrics[getSecondParametricName(productParametrics)]
	});

	if (!selectedProduct.Id) {
		showError({
			statusCode: 404,
			statusMessage: t("errors.page_not_found.title"),
			message: t("errors.page_not_found.message")
		});
		return;
	}

	useProductStore().addSelectedProduct(selectedProduct);

	try {
		const historicalBestPrices = await listHistoricalBestPrices({
			partNo: selectedProduct.PartNo
		});
		const historicalLowestPrice = getLowestPriceIncVat(
			selectedProduct.PriceIncVat,
			historicalBestPrices
		);
		useProductStore().addLowestPrice(historicalLowestPrice);
	} catch {
		/* We're already logging error */
	}

	// set page title and type to pageStore
	setPageTitle(selectedProduct.VariantName ?? selectedProduct.Name);
	setPageType("product");
};

await fetchProduct();

const selectedProductId = computed(() => useProductStore().selectedProduct?.Id);

if (selectedProductId.value) {
	const relations = await getProductRelations({
		productId: selectedProductId.value,
		...useNorceStore().storeContext
	});

	useProductStore().addRelations(relations);
}

const seoInfo = computed(() => {
	const product = useProductStore().products;
	const removeHtmlTagsRegex = /(<([^>]+)>)/gi;

	if (!product) {
		return {
			title: "",
			ogTitle: "",
			description: "",
			ogImage: ""
		};
	}

	return {
		title: `${product.VariantName || product.Name}`,
		ogTitle: product.VariantName || product.Name,
		description: product.SubHeader?.replace(removeHtmlTagsRegex, ""),
		images: product.Files?.map((image) => image.Path) || [],
		ogImage: product.Files?.[0]?.Path,
		sku: product.PartNo,
		price: product.PriceIncVat,
		inStock: getProductStock(product) > 0
	};
});
const domain = computed(() => requestURL.origin);
if (import.meta.server) {
	if (seoInfo.value?.ogImage) {
		defineOgImageComponent("OgProduct", {
			image: seoInfo.value.ogImage,
			logo: appStore.siteLogo?.filename || "",
			title: seoInfo.value.ogTitle || "",
			description: seoInfo.value.description || "",
			width: 1200,
			height: 630
		});
	}
}
useSeoMeta({
	title: () => seoInfo.value.title || "",
	ogTitle: () => seoInfo.value.ogTitle || seoInfo.value.title || "",
	description: () => seoInfo.value.description || "",
	ogDescription: () => seoInfo.value.description || "",
	ogType: "product",
	ogUrl: () => `${domain.value}${route.path}`
});

useSchemaOrg([
	defineProduct({
		"@type": "Product",
		name: seoInfo.value.title || "",
		description: seoInfo.value.description || "",
		sku: seoInfo.value.sku || "",
		image: seoInfo.value.images || [],
		offers: {
			"@type": "Offer",
			url: `${domain.value}${route.path}`,
			priceCurrency: currentCurrency.value,
			price: seoInfo.value.price?.toString() || "",
			availability: seoInfo.value.inStock
				? "https://schema.org/InStock"
				: "https://schema.org/OutOfStock",
			itemCondition: "https://schema.org/NewCondition",
			seller: {
				"@type": "Organization",
				name: appStore.siteName || ""
			}
		}
	})
]);

watch(stores, () => {
	fetchProduct();
});

watch(storeContext, () => {
	fetchProduct();
});
</script>
